import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

export interface StarRatingProps {
  /** Current selected value (1–5), or undefined if no selection. */
  value?: number;
  /** Called when the user clicks a star. */
  onRate: (rating: number) => void;
  /** When true, stars are non-interactive (post-submission state). */
  disabled?: boolean;
  /** Star icon size in pixels. */
  size?: number;
  /** Additional CSS class for the container. */
  className?: string;
}

export function StarRating({
  value,
  onRate,
  disabled = false,
  size = 28,
  className,
}: StarRatingProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const displayValue = (disabled ? null : hovered) ?? value ?? 0;

  return (
    <div
      className={cn('inline-flex gap-1', className)}
      onMouseLeave={() => !disabled && setHovered(null)}
      role="group"
      aria-label="Session rating"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= displayValue;
        return (
          <button
            key={star}
            type="button"
            disabled={disabled}
            aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
            data-filled={filled}
            onClick={() => !disabled && onRate(star)}
            onMouseEnter={() => !disabled && setHovered(star)}
            className={cn(
              'border-none bg-transparent p-0.5 transition-transform duration-150',
              disabled ? 'cursor-default opacity-80' : 'cursor-pointer hover:scale-110'
            )}
          >
            <Star
              size={size}
              fill={filled ? 'var(--f-warning)' : 'none'}
              stroke={filled ? 'var(--f-warning)' : 'var(--f-fg-muted)'}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
}
